import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Timer, Trophy, ChevronRight } from "lucide-react";
import { RoyalStage } from "./RoyalStage";
import LuckyLive, { fmtLeft } from "./LuckyLive";
import { imgUrl } from "../lib/api";
import { useMT } from "../lib/miniI18n";
import { useAuth } from "../context/AuthContext";

const hm = (s) => String(s || "00:00").split(":").map(Number);
const mins = (s) => { const [h, m] = hm(s); return h * 60 + m; };

/* Home strip: the next fixed-time show (Jackpot / Goa Raja / …) — live countdown, last result, tap to play. Becomes the live reveal panel around draw time. */
export function NextShowStrip({ games, catImages = {}, onSettled }) {
  const navigate = useNavigate();
  const mt = useMT();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ist = new Date(now + 5.5 * 3600000);
  const nowMin = ist.getUTCHours() * 60 + ist.getUTCMinutes();
  const { user } = useAuth();
  const favs = user?.fav_shows || [];
  const all = games.filter((g) => g.is_active !== false && g.live_reveal && g.mode !== "interval" && g.draw_time);
  const favShows = favs.length ? all.filter((g) => favs.includes(g.category)) : [];
  const shows = favShows.length ? favShows : all;
  if (!shows.length) return null;
  // live window: from close_time until 2 min after draw; else next show whose draw is still ahead today
  const live = shows.find((g) => nowMin >= mins(g.close_time) && nowMin < mins(g.draw_time) + 2);
  const upcoming = shows.filter((g) => mins(g.draw_time) > nowMin).sort((a, b) => mins(a.draw_time) - mins(b.draw_time))[0];
  const g = live || upcoming;
  if (!g) return null;
  if (live) return <div data-testid="home-show-live"><LuckyLive game={live} compact onSettled={onSettled} /></div>;
  const secs = Math.max(0, (mins(g.draw_time) - nowMin) * 60 - ist.getUTCSeconds());
  const last = g.last_result?.winning_number;
  return (
    <RoyalStage drawTime={g.draw_time} className="rounded-2xl shadow-lg">
    <button onClick={() => navigate(`/play/${g.id}`)} className="w-full p-3 flex items-center gap-3 text-left text-white active:scale-[0.98] transition-transform relative" data-testid="home-next-show">
      {catImages[g.category] ? <img src={imgUrl(catImages[g.category])} alt={g.category} className="w-12 h-12 rounded-xl object-cover rl-gold-border" /> : <div className="w-12 h-12 rounded-xl rl-gold-bg flex items-center justify-center font-display font-black text-slate-900">{g.category?.[0]}</div>}
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-widest text-amber-200 flex items-center gap-1"><Timer className="w-3 h-3" />{favShows.length ? "★ " : ""}{mt("Next show result")} · {g.draw_time}</div>
        <div className="font-display font-bold truncate">{g.name}</div>
        <div className="text-[11px] text-blue-100 flex items-center gap-1.5">{last ? <><Trophy className="w-3 h-3 text-amber-300" />{mt("Last result")} <b className="font-mono text-white">{last}</b></> : <>{mt("Result at")} <b className="font-mono text-white">{g.draw_time}</b></>}{g.runtime?.is_open === false && <span className="text-red-200"> · {mt("Booking closed")}</span>}</div>
      </div>
      <div className="text-right"><div className="font-mono font-black text-2xl text-amber-300" data-testid="home-next-show-countdown">{fmtLeft(secs)}</div><div className="text-[9px] text-blue-100 flex items-center justify-end">{g.runtime?.is_open ? mt("BOOK NOW") : mt("VIEW")} <ChevronRight className="w-3 h-3" /></div></div>
    </button>
    </RoyalStage>
  );
}
